import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getProfile, updateProfile, uploadProfilePicture, uploadLogo, getTemplates, createTemplate, deleteTemplate, createSubscription, verifySubscription, cancelSubscription, getSubscriptionStatus } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { EmptyState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Check, Trash2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { PLANS } from '@/lib/planConfig';

/* Below 640px the templates table collapses to labelled stacked rows. */
const ROW = 'block py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function Settings() {
  const { checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', service_type: '', template_content: { summary: '', client_needs: [''], solution: '', timeline: [{ milestone: '', duration: '', description: '' }], pricing: [{ item: '', amount: 0, description: '' }], terms: '' } });
  const [subStatus, setSubStatus] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const pictureInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    setUploadingPicture(true);
    try {
      const { data } = await uploadProfilePicture(file);
      setProfile(data);
      await checkAuth();
      toast.success('Profile picture updated');
    } catch {
      toast.error('Failed to upload profile picture');
    }
    setUploadingPicture(false);
    e.target.value = '';
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    setUploadingLogo(true);
    try {
      const { data } = await uploadLogo(file);
      setProfile(p => ({ ...p, logo_url: data.logo_url }));
      toast.success('Logo updated');
    } catch {
      toast.error('Failed to upload logo');
    }
    setUploadingLogo(false);
    e.target.value = '';
  };

  // Persisted immediately — a toggle that needs a separate Save is a toggle
  // people think they flipped and did not.
  const handleRemindersToggle = async (enabled) => {
    setProfile(p => ({ ...p, reminders_enabled: enabled }));
    setSavingReminders(true);
    try {
      await updateProfile({ reminders_enabled: enabled });
      toast.success(enabled ? 'Payment reminders on' : 'Payment reminders off');
    } catch {
      setProfile(p => ({ ...p, reminders_enabled: !enabled }));
      toast.error('Failed to update reminders');
    }
    setSavingReminders(false);
  };

  const loadSubscriptionStatus = () => {
    getSubscriptionStatus().then(r => setSubStatus(r.data)).catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      getProfile().then(r => setProfile(r.data)),
      getTemplates().then(r => setTemplates(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
    loadSubscriptionStatus();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ full_name: profile.full_name, business_name: profile.business_name, address: profile.address, default_currency: profile.default_currency });
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    setSaving(false);
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.name.trim()) { toast.error('Template name is required'); return; }
    try {
      const res = await createTemplate(newTemplate);
      setTemplates(prev => [res.data, ...prev]);
      setShowAddTemplate(false);
      setNewTemplate({ name: '', service_type: '', template_content: { summary: '', client_needs: [''], solution: '', timeline: [{ milestone: '', duration: '', description: '' }], pricing: [{ item: '', amount: 0, description: '' }], terms: '' } });
      toast.success('Template created');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create template';
      toast.error(msg);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete template'); }
  };

  const handleUpgrade = async (planKey) => {
    setUpgrading(true);
    try {
      const { data } = await createSubscription(planKey);
      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'FlowLancer',
        description: `${planKey.charAt(0).toUpperCase() + planKey.slice(1)} Plan Subscription`,
        handler: async (response) => {
          try {
            await verifySubscription({
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Plan upgraded successfully!');
            const r = await getProfile();
            setProfile(r.data);
            loadSubscriptionStatus();
          } catch {
            toast.error('Payment verification failed');
          }
        },
        theme: { color: '#1F4D3A' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => toast.error('Payment failed. Please try again.'));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start upgrade');
    }
    setUpgrading(false);
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription();
      toast.success('Subscription will be cancelled at end of billing period');
      setShowCancelDialog(false);
      const r = await getProfile();
      setProfile(r.data);
      loadSubscriptionStatus();
    } catch {
      toast.error('Failed to cancel subscription');
    }
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-wash" />;

  const tc = newTemplate.template_content;
  // Mirrors isWhiteLabeled() in backend/src/routes/public.ts — the server is
  // still the authority on what clients actually see.
  const isPremium = profile?.plan === 'premium';

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" description="Manage your profile, templates, and plan" />

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-profile-tab">Profile</TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-templates-tab">Templates</TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-billing-tab">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8">
          <section className="max-w-[560px]">
            <SectionRule label="Photo" />
            <div className="flex items-center gap-5">
              <input
                ref={pictureInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handlePictureUpload}
              />
              <button
                type="button"
                onClick={() => pictureInputRef.current?.click()}
                disabled={uploadingPicture}
                className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                aria-label="Change profile picture"
              >
                {profile?.picture ? (
                  <img src={profile.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-wash font-display text-subhead text-ink">
                    {(profile?.name || 'U')[0]}
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center transition-colors group-hover:bg-ink/40">
                  {uploadingPicture ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-paper border-t-transparent" />
                  ) : (
                    <Camera strokeWidth={1.5} className="h-5 w-5 text-paper opacity-0 transition-opacity group-hover:opacity-100" />
                  )}
                </div>
              </button>
              <div>
                <p className="text-body text-ink">{profile?.name}</p>
                <p className="text-small text-ink-muted">{profile?.email}</p>
                <p className="mt-1 text-small text-ink-subtle">Click the photo to change it</p>
              </div>
            </div>
          </section>

          <section className="mt-10 max-w-[560px]">
            <SectionRule label="Profile" />
            <div className="space-y-4">
              <Field label="Full name" htmlFor="full-name">
                <Input id="full-name" value={profile?.full_name || ''} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} data-testid="full-name-input" />
              </Field>
              <Field label="Business name" htmlFor="business-name">
                <Input id="business-name" value={profile?.business_name || ''} onChange={e => setProfile(p => ({ ...p, business_name: e.target.value }))} placeholder="Your Company, LLC" data-testid="business-name-input" />
              </Field>
              <Field label="Business address" htmlFor="business-address">
                <Textarea id="business-address" value={profile?.address || ''} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} placeholder={'123 Main St, Suite 4\nAustin, TX 78701\nUSA'} data-testid="business-address-input" />
              </Field>
              <Field label="Default currency" htmlFor="default-currency">
                <Select value={profile?.default_currency || 'USD'} onValueChange={v => setProfile(p => ({ ...p, default_currency: v }))}>
                  <SelectTrigger id="default-currency" data-testid="currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="CAD">CAD ($)</SelectItem>
                    <SelectItem value="AUD">AUD ($)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-8 flex justify-end border-t border-line pt-4">
              <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </section>

          <section className="mt-10 max-w-[560px]" data-testid="branding-section">
            <SectionRule
              label="Branding"
              action={
                !isPremium && <span className="text-micro uppercase text-ink-subtle">Premium</span>
              }
            />
            <p className="max-w-prose text-small text-ink-muted">
              {isPremium
                ? 'Your logo replaces the FlowLancer badge on every proposal, invoice and delivery page your clients see.'
                : 'On Premium, your logo replaces the FlowLancer badge on every document your clients see.'}
            </p>

            <div className="mt-6 flex items-center gap-5">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <div className="flex h-16 w-[180px] shrink-0 items-center justify-center border border-line bg-surface px-3">
                {profile?.logo_url
                  ? <img src={profile.logo_url} alt="Your logo" className="max-h-12 max-w-full object-contain" />
                  : <span className="text-micro uppercase text-ink-subtle">No logo</span>}
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploadingLogo || !isPremium}
                  onClick={() => logoInputRef.current?.click()}
                  data-testid="upload-logo-btn"
                >
                  {uploadingLogo ? 'Uploading…' : profile?.logo_url ? 'Replace logo' : 'Upload logo'}
                </Button>
                <p className="mt-2 text-small text-ink-subtle">PNG, JPG, WebP or GIF. Max 2MB.</p>
              </div>
            </div>

            {!isPremium && (
              <p className="mt-4 text-small text-ink-muted">
                Your documents currently carry the FlowLancer badge.
              </p>
            )}
          </section>

          <section className="mt-10 max-w-[560px]" data-testid="reminders-section">
            <SectionRule label="Payment reminders" />
            <div className="flex items-start justify-between gap-6">
              <div>
                <Label htmlFor="reminders-toggle" className="text-small font-medium text-ink">
                  Chase overdue invoices automatically
                </Label>
                <p className="mt-1 max-w-prose text-small text-ink-muted">
                  Sends your client an escalating reminder 3, 7 and 14 days after an invoice
                  falls past due. Stops as soon as it is paid.
                </p>
              </div>
              <Switch
                id="reminders-toggle"
                checked={profile?.reminders_enabled !== false}
                disabled={savingReminders}
                onCheckedChange={handleRemindersToggle}
                data-testid="reminders-toggle"
              />
            </div>
          </section>
        </TabsContent>

        <TabsContent value="templates" className="mt-8">
          <section>
            <SectionRule
              label="Proposal templates"
              action={
                <Button onClick={() => setShowAddTemplate(true)} variant="outline" size="sm" data-testid="add-template-btn">
                  New template
                </Button>
              }
            />
            {templates.length === 0 ? (
              <EmptyState
                title="No templates yet"
                description="Reusable templates give the AI a starting point and speed up every proposal."
                action={<Button onClick={() => setShowAddTemplate(true)}>New template</Button>}
              />
            ) : (
              <Table>
                <TableHeader className="hidden sm:table-header-group">
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Service type</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-10"><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t.id} className={ROW} data-testid={`template-${t.id}`}>
                      <TableCell data-label="Template" className={`${CELL} text-ink`}>{t.name}</TableCell>
                      <TableCell data-label="Service type" className={`${CELL} text-ink-muted`}>{t.service_type || '—'}</TableCell>
                      <TableCell data-label="Created" className={`${CELL} text-ink-muted`}>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className={`${CELL} before:content-none sm:text-right`}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-ink-subtle hover:text-destructive"
                          onClick={() => handleDeleteTemplate(t.id)}
                          aria-label={`Delete template ${t.name}`}
                          data-testid={`delete-template-${t.id}`}
                        >
                          <Trash2 strokeWidth={1.5} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        </TabsContent>

        <TabsContent value="billing" className="mt-8">
          <section>
            <SectionRule
              label="Plan & usage"
              action={
                <span className="text-small capitalize text-ink-muted">
                  {profile?.plan || 'free'} plan
                  {profile?.subscription_status === 'cancelled' && (
                    <span className="ml-2 text-status-overdue">Cancelling</span>
                  )}
                </span>
              }
            />
            {subStatus?.usage && (
              <div className="grid grid-cols-2 gap-6 md:grid-cols-5">
                {[
                  { label: 'Proposals', key: 'proposals', monthly: true },
                  { label: 'Invoices', key: 'invoices', monthly: true },
                  { label: 'Projects', key: 'projects', monthly: false },
                  { label: 'Clients', key: 'clients', monthly: false },
                  { label: 'Templates', key: 'templates', monthly: false },
                ].map(({ label, key, monthly }) => {
                  const u = subStatus.usage[key];
                  const isUnlimited = u.limit === -1;
                  const pct = isUnlimited ? 0 : Math.min((u.used / u.limit) * 100, 100);
                  const atLimit = !isUnlimited && u.used >= u.limit;
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-micro uppercase text-ink-subtle">{label}{monthly ? ' /mo' : ''}</span>
                        <span className={`text-small tabular-nums ${atLimit ? 'text-status-overdue' : 'text-ink'}`}>
                          {u.used}/{isUnlimited ? '∞' : u.limit}
                        </span>
                      </div>
                      <Progress value={isUnlimited ? 0 : pct} className={`h-0.5 ${atLimit ? '[&>div]:bg-destructive' : ''}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="mt-12">
            <SectionRule label="Plans" />
            <div className="grid grid-cols-1 sm:grid-cols-3">
              {PLANS.map((plan, i) => {
                const isCurrent = (profile?.plan || 'free') === plan.key;
                const isDowngrade = (profile?.plan === 'premium' && plan.key === 'pro') || (profile?.plan !== 'free' && plan.key === 'free');
                return (
                  <div
                    key={plan.key}
                    className={`flex flex-col pt-5 sm:px-6 sm:first:pl-0 sm:last:pr-0 ${
                      isCurrent ? 'border-t-2 border-forest' : 'border-t border-line'
                    } ${i > 0 ? 'mt-10 sm:mt-0 sm:border-l sm:border-l-line' : ''}`}
                    data-testid={`plan-card-${plan.key}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <h3 className="text-subhead text-ink">{plan.name}</h3>
                      {isCurrent && <span className="text-micro uppercase text-forest">Current</span>}
                    </div>
                    <p className="mt-1 text-small text-ink-muted">{plan.description}</p>
                    <div className="mt-4 flex items-baseline gap-1.5">
                      <span className="font-sans text-metric tabular-nums text-ink">{plan.currency}{plan.price.toLocaleString('en-US')}</span>
                      <span className="text-small text-ink-subtle">{plan.period}</span>
                    </div>
                    <ul className="mb-8 mt-6 flex-grow space-y-2.5">
                      {plan.features.map((feat, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-small text-ink-muted">
                          <Check strokeWidth={1.5} className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <Button disabled variant="outline" className="w-full">Current plan</Button>
                    ) : isDowngrade ? (
                      <Button disabled variant="outline" className="w-full">—</Button>
                    ) : (
                      <Button
                        variant={plan.popular ? 'default' : 'outline'}
                        className="w-full"
                        disabled={upgrading}
                        onClick={() => handleUpgrade(plan.key)}
                        data-testid={`upgrade-${plan.key}-btn`}
                      >
                        {upgrading ? 'Processing…' : plan.cta}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {profile?.plan !== 'free' && profile?.subscription_status === 'active' && (
            <section className="mt-12">
              <SectionRule label="Danger zone" />
              <div className="rounded-md border border-destructive/30 p-5">
                <p className="text-body text-ink">Cancel subscription</p>
                <p className="mt-1 max-w-prose text-small text-ink-muted">
                  Your plan stays active until the end of the current billing period, then drops to Free.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 border-destructive/30 text-destructive hover:bg-destructive/10"
                  onClick={() => setShowCancelDialog(true)}
                  data-testid="cancel-subscription-btn"
                >
                  Cancel subscription
                </Button>
              </div>
            </section>
          )}
        </TabsContent>
      </Tabs>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel subscription</DialogTitle>
            <DialogDescription>
              Your subscription will remain active until the end of the current billing period. After that, your account will be downgraded to the Free plan.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 border-t border-line pt-4">
            <Button variant="ghost" onClick={() => setShowCancelDialog(false)}>Keep plan</Button>
            <Button variant="destructive" onClick={handleCancelSubscription} data-testid="confirm-cancel-btn">Cancel subscription</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Template Dialog */}
      <Dialog open={showAddTemplate} onOpenChange={setShowAddTemplate}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="add-template-dialog">
          <DialogHeader>
            <DialogTitle>New proposal template</DialogTitle>
            <DialogDescription>Create a reusable template for common proposal types.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Template name" htmlFor="template-name" hint="Required">
                <Input id="template-name" value={newTemplate.name} onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })} placeholder="e.g. Website redesign" data-testid="template-name-input" />
              </Field>
              <Field label="Service type" htmlFor="template-service">
                <Input id="template-service" value={newTemplate.service_type} onChange={e => setNewTemplate({ ...newTemplate, service_type: e.target.value })} placeholder="e.g. Web development" />
              </Field>
            </div>
            <Field label="Default summary" htmlFor="template-summary">
              <Textarea id="template-summary" value={tc.summary} onChange={e => setNewTemplate({ ...newTemplate, template_content: { ...tc, summary: e.target.value } })} placeholder="Brief summary template" className="min-h-[60px]" />
            </Field>
            <Field label="Default solution" htmlFor="template-solution">
              <Textarea id="template-solution" value={tc.solution} onChange={e => setNewTemplate({ ...newTemplate, template_content: { ...tc, solution: e.target.value } })} placeholder="Solution approach template" className="min-h-[80px]" />
            </Field>
            <Field label="Default terms" htmlFor="template-terms">
              <Textarea id="template-terms" value={tc.terms} onChange={e => setNewTemplate({ ...newTemplate, template_content: { ...tc, terms: e.target.value } })} placeholder="Standard terms" className="min-h-[60px]" />
            </Field>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowAddTemplate(false)}>Cancel</Button>
              <Button onClick={handleAddTemplate} data-testid="save-template-btn">Create template</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
